import { Object3D, Quaternion, Vector3 } from "three";
import type { ConsoleStagePose } from "@models/xr/IConsolePlacementModel";

/**
 * `stageSpace.ts` — the STAGE ⇄ WORLD boundary, and the rule for which side of
 * it any given value lives on.
 *
 * ## The stage
 *
 * `engine.root` (`"campfire.root"`) is the web's counterpart of Unity's
 * `ISceneModel.RootSpace` — the root `GameObject` named "Space" that console
 * placement moves (`QuestConsolePlacementModel.UpdateObjects`). It is the
 * parent of everything that belongs to the PROJECT:
 *
 *   `campfire-scenes`, `campfire.sceneEnvironment` (table / console / edge
 *   beam / floor grid / anchor), `campfire-media-tiles`,
 *   `campfire-annotations`, `multiuserRoot`, the scene tools, the lights.
 *
 * Everything that belongs to the ROOM — the XR slates (`campfire.xrPanels`),
 * the hand rays and hand meshes (`campfire.xrInput`), the placement visuals,
 * Spark (`sparkRoot`, which stands relative to the WEARER), the workflow
 * attachment leader (which joins a slate to a part, so world space is the only
 * frame holding both ends) and the skydome — is parented to `engine.scene`
 * instead, so it stays put while the stage moves under it. That split IS the
 * feature: pick a parent by asking "does this belong to the project or to the
 * room?".
 *
 * One pairing is easy to get wrong in either direction: a `DirectionalLight`
 * aims from its own world position at its target's, so the light AND its
 * target must share a parent. Both are on the stage
 * (`sceneEnvironmentRenderer.mountLights`) — split them and yawing the stage
 * silently swings the sun round the room.
 *
 * Outside an XR session the stage is IDENTITY and stage space and world space
 * coincide, which is why nothing in the port needed this module until now.
 *
 * ## When you need a conversion, and when you do not
 *
 * **You do NOT** when the value is written to, or read from, an object PARENTED
 * under the stage. Three's local transforms already compose with the stage
 * matrix, which is precisely Unity's `rootSpace.TransformPoint`. So a peer
 * avatar's position, a media tile's transform, an annotation marker and every
 * entity snapshot are correct with no code change — they are stage-local by
 * construction.
 *
 * **You DO** whenever a value crosses between the two families:
 *
 *   - the CAMERA (world) → a `ParticipantTransform` on the wire (stage-local).
 *     This is Unity's `rootSpace.InverseTransformPoint(camera.position)`
 *     (`QuestLocalUserModel.cs:322`) and it is the single most important call
 *     site: without it, two headsets whose stages sit in different rooms would
 *     each place the other somewhere in the middle of nowhere;
 *   - a stored SCENE-VIEW pose or a followed peer's pose (stage-local) → the
 *     desktop orbit camera (world);
 *   - a BILLBOARD parented under the stage reading `camera.quaternion` (world)
 *     — it must be expressed in the stage's frame or the card turns with the
 *     stage instead of with the head. {@link worldToStageQuaternion} is that.
 *
 * ## Why the maths is this cheap
 *
 * The stage is a direct child of `scene` (identity) and is never scaled, so
 * `root.matrixWorld === root.matrix`, `root.quaternion` IS its world
 * quaternion, and the inverse is a plain conjugate. Everything below is a
 * rotate-and-translate — no matrix inversion, no scale handling. If either of
 * those invariants ever stops holding, these helpers must move to
 * `matrixWorld`-based maths and this comment must go with them.
 *
 * NOT to be confused with `models/utils/unityBasis.ts`, which is a different
 * boundary entirely (handedness / forward convention). The two compose in one
 * direction only: convert to stage space FIRST, then through the basis map.
 */

const SCRATCH_QUAT = new Quaternion();
const SCRATCH_QUAT_OUT = new Quaternion();
const UP = new Vector3(0, 1, 0);

/** Write `pose` onto the stage root. The one place the stage transform is set. */
export function applyStagePose(root: Object3D, pose: ConsoleStagePose): void {
  root.position.set(pose.x, pose.y, pose.z);
  root.quaternion.setFromAxisAngle(UP, (pose.yawDegrees * Math.PI) / 180);
  // SELF only (`updateWorldMatrix(true, false)`), never the subtree: a
  // placement drag writes this every frame, and `updateMatrixWorld(true)` would
  // walk every entity in the scene each time — a nine-hundred-part model is not
  // a per-frame traversal. Descendants are refreshed by three's own render
  // pass; what has to be current NOW is `root.matrixWorld`, which
  // `SlicePlaneRenderer` reads to lift its clipping plane into world space.
  root.updateWorldMatrix(true, false);
}

/** World point → stage-local (Unity `rootSpace.InverseTransformPoint`). */
export function worldToStagePosition(root: Object3D, world: Vector3, out: Vector3): Vector3 {
  return out
    .copy(world)
    .sub(root.position)
    .applyQuaternion(SCRATCH_QUAT.copy(root.quaternion).invert());
}

/** Stage-local point → world (Unity `rootSpace.TransformPoint`). */
export function stageToWorldPosition(root: Object3D, local: Vector3, out: Vector3): Vector3 {
  return out.copy(local).applyQuaternion(root.quaternion).add(root.position);
}

/**
 * World rotation → stage-local (Unity
 * `Quaternion.Inverse(rootSpace.rotation) * worldRotation`).
 *
 * Also the billboard fix: a card under the stage that wants to face the head
 * needs the head's rotation IN ITS OWN FRAME, not in world space.
 */
export function worldToStageQuaternion(root: Object3D, world: Quaternion, out: Quaternion): Quaternion {
  // Composed in a scratch and copied out, so `out` may safely alias `world` —
  // the natural in-place call. Writing straight into `out` would overwrite the
  // operand before `multiply` reads it, and the result would look plausible
  // (a rotation, just the wrong one). The position helpers are alias-safe
  // already because they `copy` the operand first.
  SCRATCH_QUAT_OUT.copy(root.quaternion).invert().multiply(world);
  return out.copy(SCRATCH_QUAT_OUT);
}

/** Stage-local rotation → world (Unity `rootSpace.rotation * localRotation`). */
export function stageToWorldQuaternion(root: Object3D, local: Quaternion, out: Quaternion): Quaternion {
  SCRATCH_QUAT_OUT.copy(root.quaternion).multiply(local);
  return out.copy(SCRATCH_QUAT_OUT);
}

/** True while the stage sits at the origin — i.e. every conversion is a no-op. */
export function isStageIdentity(root: Object3D): boolean {
  return (
    root.position.x === 0 &&
    root.position.y === 0 &&
    root.position.z === 0 &&
    root.quaternion.x === 0 &&
    root.quaternion.y === 0 &&
    root.quaternion.z === 0 &&
    Math.abs(root.quaternion.w) === 1
  );
}
